import {
  Form,
  Input,
  Select,
  Switch,
  Button as AntButton,
  Upload,
  DatePicker,
  Button,
  Tooltip,
} from 'antd';
import {Button as DabaButton} from '@dabafinance/react-components';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {useNavigate, useSearchParams} from 'react-router-dom';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {supportedLanguages} from 'utils/constants';
import {fieldRules} from 'utils/helper';
import {useQuickActionForm} from './hooks/useQuickActionForm';
import styles from './quickActions.module.scss';
import moment from 'moment';

const {Option} = Select;

const QUICK_ACTION_KEYS = [
  {value: 'DABA_PRO', label: 'Daba Pro'},
  {value: 'LEADERBOARD', label: 'Leaderboard'},
  {value: 'STOCKS', label: 'Stocks'},
  {value: 'BONDS', label: 'Bonds'},
  {value: 'MANAGED_FUNDS', label: 'Managed Funds'},
  {value: 'IPOS', label: 'IPO'},
  {value: 'COLLECTIONS', label: 'Collections'},
  {value: 'SIMULATOR', label: 'Simulator'},
  {value: 'SAVINGS', label: 'Savings'},
  {value: 'DABA_ACADEMY', label: 'Daba Academy'},
  {value: 'NEWS', label: 'News'},
  {value: 'STARTUPS', label: 'Startups'},
  {value: 'CIRCLES', label: 'Circles'},
  {value: 'LEARN', label: 'Learn'},
  {value: 'COMPANIES', label: 'Companies'},
  {value: 'INTELLIGENCE', label: 'Intelligence'},
  {value: 'SIMULATOR', label: 'Simulator'},
];

const QuickActionForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryIdFromQuery = searchParams.get('categoryId');
  const {
    form,
    language,
    selectedTranslationLanguage,
    translatedActions,
    handleChangeTranslatedValues,
    handleRemoveTranslation,
    handleIconUpload,
    handleSubmit,
    loading,
    loadingAction,
    loadingCategories,
    uploadingIcon,
    translating,
    isEditMode,
    categories,
    actionName,
    handleTranslate,
  } = useQuickActionForm();

  const languageToBeTranslatedTo = supportedLanguages.filter(
    lang => lang.value !== language,
  );

  const iconValue = Form.useWatch('icon', form);
  const isDefaultValue = Form.useWatch('isDefault', form);

  const handleBeforeUpload = file => {
    handleIconUpload(file);
    return false; // Prevent default upload
  };

  if (loadingAction || loadingCategories) {
    return (
      <div className={styles.loading}>
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <AntButton
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (categoryIdFromQuery) {
              navigate(
                `/dashboard/quick-actions/categories/${categoryIdFromQuery}/edit`,
              );
            } else {
              navigate('/dashboard/quick-actions/actions');
            }
          }}
          className={styles.backButton}>
          Back
        </AntButton>
        <div className={styles.titleRow}>
          <h1>{isEditMode ? 'Edit Quick Action' : 'Create Quick Action'}</h1>
          {isEditMode && actionName && (
            <span className={styles.nameBadge}>{actionName}</span>
          )}
        </div>
        <p>
          {isEditMode
            ? 'Update the quick action details'
            : 'Create a new quick action'}
        </p>
      </div>

      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <div className={styles.sectionCard}>
          <div className={styles.formFieldsGrid}>
            <Form.Item
              label="Show in Homepage?"
              name="isDefault"
              className={styles.formField}>
              <Select
                placeholder="Select option"
                onChange={val => form.setFieldsValue({isDefault: val})}>
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>

            {isDefaultValue != undefined && !isDefaultValue && (
              <Form.Item
                label="Category"
                name="category"
                className={styles.formField}>
                <Select
                  allowClear
                  placeholder="Select a category (optional)"
                  loading={loadingCategories}>
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              rules={[fieldRules('Language')]}
              label="Language"
              name="language"
              initialValue="EN"
              className={styles.formField}>
              <Select>
                {supportedLanguages.map(lang => (
                  <Option key={lang.value} value={lang.value}>
                    {lang.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className={styles.formFieldsGrid}>
            <Form.Item
              rules={[fieldRules('Key')]}
              label="Key"
              name="key"
              className={styles.formField}>
              <Select
                placeholder="Select a key"
                onChange={val => {
                  const selectedKey = QUICK_ACTION_KEYS.find(
                    k => k.value === val,
                  );
                  if (selectedKey) {
                    form.setFieldsValue({name: selectedKey.label});
                  }
                }}>
                {QUICK_ACTION_KEYS.map(keyOption => (
                  <Option key={keyOption.value} value={keyOption.value}>
                    {keyOption.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              rules={[fieldRules('Name')]}
              label="Name"
              name="name"
              className={styles.formField}>
              <Input placeholder="Enter quick action name" />
            </Form.Item>
          </div>

          <div className={styles.translationSelector}>
            <Form.Item
              label="Language Translation"
              name="translation"
              className={styles.formField}
              style={{width: 250}}>
              <Select allowClear placeholder="Select a language to translate">
                {languageToBeTranslatedTo.map(lang => (
                  <Option key={lang.value} value={lang.value}>
                    {lang.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {selectedTranslationLanguage && (
              <DabaButton
                label={translating ? 'Translating...' : 'Translate'}
                onClick={handleTranslate}
                disabled={translating || !selectedTranslationLanguage}
              />
            )}
          </div>

          {translatedActions?.length > 0 && (
            <div className={styles.translationsSection}>
              <h4 className={styles.translationsTitle}>Translations</h4>
              {translatedActions.map((data, index) => {
                const languageLabel =
                  supportedLanguages.find(lang => lang.value === data.language)
                    ?.label || data.language;
                return (
                  <div key={data.language} className={styles.translationItem}>
                    <div className={styles.translationHeader}>
                      <span className={styles.translationLanguage}>
                        {languageLabel}
                      </span>
                      <div className={styles.translationControls}>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleRemoveTranslation(index)}
                          style={{marginLeft: 8}}>
                          Remove
                        </Button>
                      </div>
                    </div>
                    <Form.Item
                      label="Name"
                      className={styles.formField}
                      style={{marginBottom: 0}}>
                      <Input
                        value={data.name}
                        onChange={e =>
                          handleChangeTranslatedValues(
                            index,
                            e.target.value,
                            'name',
                          )
                        }
                        placeholder={`Translated name (${languageLabel})`}
                      />
                    </Form.Item>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h3>Display & Flags</h3>
            <p>Control icon, visibility, and default settings.</p>
          </div>
          <div className={styles.formFieldsGrid}>
            <Form.Item
              label={
                <span>
                  Icon{' '}
                  <Tooltip title="If no icon is uploaded, the existing icon in the app will be used. Note: Uploading a new icon will overwrite the current one in the app.">
                    <QuestionCircleOutlined className={styles.questionIcon} />
                  </Tooltip>
                </span>
              }
              name="icon"
              className={styles.formField}>
              <div className={styles.iconField}>
                <div className={styles.iconUploadContainer}>
                  {iconValue ? (
                    <div className={styles.iconPreview}>
                      <img src={iconValue} alt="Icon preview" />
                      <Button
                        type="link"
                        danger
                        onClick={() => form.setFieldsValue({icon: ''})}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      beforeUpload={handleBeforeUpload}
                      maxCount={1}
                      accept="image/*"
                      disabled={uploadingIcon}
                      showUploadList={false}>
                      <div className={styles.uploadPlaceholder}>
                        {uploadingIcon ? (
                          <span>Uploading...</span>
                        ) : (
                          <>
                            <UploadOutlined className={styles.uploadIcon} />
                            <span>Upload Icon</span>
                          </>
                        )}
                      </div>
                    </Upload>
                  )}
                </div>
                <span className={styles.iconNote}>
                  The app already has icons; if another is uploaded, the one in
                  the app will be updated.
                </span>
              </div>
            </Form.Item>

            <Form.Item
              label={
                <span>
                  New Until{' '}
                  <Tooltip title="Show new badge until selected date">
                    <QuestionCircleOutlined style={{color: '#8b82a7'}} />
                  </Tooltip>
                </span>
              }
              name="newUntil"
              className={styles.formField}
              getValueFromEvent={value => value}
              getValueProps={value => ({
                value: value ? moment(value) : null,
              })}>
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                placeholder="Select date"
                style={{width: '100%'}}
              />
            </Form.Item>
          </div>

          <div className={styles.switchFields}>
            <Form.Item
              label="Active"
              name="isActive"
              valuePropName="checked"
              initialValue={true}
              className={styles.switchField}>
              <Switch />
            </Form.Item>
          </div>
        </div>

        <Form.Item className={styles.formActions}>
          <SubmitButton
            disabled={loading}
            label={isEditMode ? 'Update Quick Action' : 'Create Quick Action'}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default QuickActionForm;
